import React, { FunctionComponent, useContext, useEffect, useState } from "react";
import { NamedProperty } from "sprint-common-web";

import { EncodeSpecificationContext } from "./EncodeSpecificationContext";
import {
    AspectType,
    EncodeSpecificationItem,
    EncodeSpecificationMpeg1Item,
    EncodeSpecificationMpeg4Item,
    EncodeSpecificationType,
    FrameratePresets,
    mapToEncodeSpecificationItem
} from "./EncodeSpecificationItem";
import { AspectRatio } from "../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { useEncodeSpecificationApi } from "../../../common/api/orders-api/encode-specification/useEncodeSpecificationApi";
import { getUpdatedAuditInfo } from "../../../common/types/AuditInfo";
import { UserContext } from "../../../core/security/UserProvider";

export interface UpdateEncodeSpecificationMpeg4Props {
    id: string;
    name: string;
    ffmpegCommand: string;
    framerateProfile: NamedProperty;
    targetAspectRatio: AspectRatio;
}

export interface UpdateEncodeSpecificationMpeg1Props {
    id: string;
    name: string;
    frameratePresets: FrameratePresets;
    targetAspectRatio: AspectRatio;
    aspectType: AspectType;
    slug?: number;
}

export type EncodeSpecificationProviderProps = React.PropsWithChildren<unknown>;

export const EncodeSpecificationProvider: FunctionComponent<EncodeSpecificationProviderProps> = (
    props: EncodeSpecificationProviderProps
) => {
    const [encodeSpecifications, setEncodeSpecifications] = useState<EncodeSpecificationItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { getAllEncodeSpecifications } = useEncodeSpecificationApi();
    const { name: userName } = useContext(UserContext);

    useEffect(() => {
        setIsLoading(true);
        getAllEncodeSpecifications()
            .then((response) => {
                const items = response.items.map(mapToEncodeSpecificationItem);

                setEncodeSpecifications(items);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const updateEncodeSpecificationMpeg1 = ({
        id,
        name,
        aspectType,
        frameratePresets,
        targetAspectRatio,
        slug
    }: UpdateEncodeSpecificationMpeg1Props): void => {
        setEncodeSpecifications((prevItems) => {
            const encodeSpecificationToUpdate = prevItems.find((item) => item.id === id);

            if (!encodeSpecificationToUpdate) return prevItems;

            if (encodeSpecificationToUpdate.type !== EncodeSpecificationType.MPEG_1) {
                throw new Error(
                    `Incorrect encode specification type '${encodeSpecificationToUpdate.type}'. Type should be MPEG-1`
                );
            }

            encodeSpecificationToUpdate.name = name;
            encodeSpecificationToUpdate.audit = getUpdatedAuditInfo(encodeSpecificationToUpdate.audit, userName);
            encodeSpecificationToUpdate.frameratePresets = frameratePresets;
            encodeSpecificationToUpdate.aspectType = aspectType;
            encodeSpecificationToUpdate.slug = slug;
            encodeSpecificationToUpdate.targetAspectRatio = targetAspectRatio;
            encodeSpecificationToUpdate.isModified = true;

            return [...prevItems];
        });
    };

    const updateEncodeSpecificationMpeg4 = ({
        id,
        name,
        framerateProfile,
        ffmpegCommand,
        targetAspectRatio
    }: UpdateEncodeSpecificationMpeg4Props): void => {
        setEncodeSpecifications((prevItems) => {
            const encodeSpecificationToUpdate = prevItems.find((item) => item.id === id);

            if (!encodeSpecificationToUpdate) return prevItems;

            if (encodeSpecificationToUpdate.type !== EncodeSpecificationType.MPEG_4) {
                throw new Error(
                    `Incorrect encode specification type '${encodeSpecificationToUpdate.type}'. Type should be MPEG-4`
                );
            }

            encodeSpecificationToUpdate.name = name;
            encodeSpecificationToUpdate.ffmpegCommand = ffmpegCommand;
            encodeSpecificationToUpdate.framerateProfile = framerateProfile;
            encodeSpecificationToUpdate.targetAspectRatio = targetAspectRatio;
            encodeSpecificationToUpdate.audit = getUpdatedAuditInfo(encodeSpecificationToUpdate.audit, userName);
            encodeSpecificationToUpdate.isModified = true;

            return [...prevItems];
        });
    };

    const addEncodeSpecificationMpeg1 = (item: EncodeSpecificationMpeg1Item): void => {
        setEncodeSpecifications((prevItems) => [
            ...prevItems,
            {
                id: item.id,
                name: item.name,
                type: item.type,
                audit: item.audit,
                frameratePresets: item.frameratePresets,
                isModified: true,
                slug: item.slug,
                aspectType: item.aspectType,
                targetAspectRatio: item.targetAspectRatio
            }
        ]);
    };
    const addEncodeSpecificationMpeg4 = (item: EncodeSpecificationMpeg4Item): void => {
        setEncodeSpecifications((prevItems) => [
            ...prevItems,
            {
                id: item.id,
                name: item.name,
                type: item.type,
                audit: item.audit,
                ffmpegCommand: item.ffmpegCommand,
                framerateProfile: item.framerateProfile,
                isModified: true,
                targetAspectRatio: item.targetAspectRatio
            }
        ]);
    };

    return (
        <EncodeSpecificationContext.Provider
            value={{
                isLoading,
                encodeSpecifications,
                updateEncodeSpecificationMpeg1,
                updateEncodeSpecificationMpeg4,
                addEncodeSpecificationMpeg1,
                addEncodeSpecificationMpeg4
            }}
        >
            {props.children}
        </EncodeSpecificationContext.Provider>
    );
};
